import type { CoreError } from '@ingenico/api-sdk-core/common';
import { useBusinessServiceRequest } from '@ingenico/react-section-utils';
import { useDebouncedState } from '@react-hookz/web';
import { isEmpty } from 'lodash-es';
import { useCallback, useEffect, useMemo } from 'react';
import { DeviceManagementService } from '../../domain/business/services/deviceManagement.service';
import { SoftwareType } from '../../domain/models/selectItems.model';

interface UseFileOutputFormularyDataProps {
  readonly softwareType: SoftwareType;
  readonly softwareNameId: string;
  readonly versionId: string;
  readonly onError: (error: CoreError | null) => void;
}

export function useFileOutputFormularyData({
  softwareType,
  softwareNameId,
  versionId,
  onError,
}: UseFileOutputFormularyDataProps) {
  const [softwareFilterValue, setSoftwareFilterValue] = useDebouncedState('', 500);
  const [versionFilterValue, setVersionFilterValue] = useDebouncedState('', 500);
  const [moduleFilterValue, setModuleFilterValue] = useDebouncedState('', 500);

  const {
    isLoading: isLoadingSoftwareNames,
    data: softwareNameOptions,
    isError: isErrorSoftwareNames,
    error: errorSoftwareNames,
  } = useBusinessServiceRequest(
    DeviceManagementService,
    useCallback(
      (deviceManagementService) =>
        SoftwareType.None === softwareType
          ? null
          : deviceManagementService.searchSoftwareNameItems(softwareType, softwareFilterValue, {
              offset: 0,
              limit: 100,
            }),
      [softwareType, softwareFilterValue],
    ),
  );

  const isAnyVersionFieldsEmpty = useMemo(() => {
    return softwareType === SoftwareType.None || isEmpty(softwareNameId);
  }, [softwareNameId, softwareType]);

  const {
    isLoading: isLoadingVersions,
    data: versionOptions,
    isError: isErrorVersions,
    error: errorVersions,
  } = useBusinessServiceRequest(
    DeviceManagementService,
    useCallback(
      (deviceManagementService) =>
        isAnyVersionFieldsEmpty
          ? null
          : deviceManagementService.searchSoftwareVersionItems(softwareType, softwareNameId, versionFilterValue, {
              offset: 0,
              limit: 100,
            }),
      [isAnyVersionFieldsEmpty, softwareNameId, softwareType, versionFilterValue],
    ),
  );

  const {
    isLoading: isLoadingModules,
    data: moduleOptions,
    isError: isErrorModules,
    error: errorModules,
  } = useBusinessServiceRequest(
    DeviceManagementService,
    useCallback(
      (deviceManagementService) =>
        isEmpty(versionId)
          ? null
          : deviceManagementService.searchConfigurationModuleSelectiItems(versionId, moduleFilterValue, {
              offset: 0,
              limit: 100,
            }),
      [versionId, moduleFilterValue],
    ),
  );

  useEffect(() => {
    if (isErrorSoftwareNames) {
      onError(errorSoftwareNames);
    }
  }, [errorSoftwareNames, isErrorSoftwareNames, onError]);

  useEffect(() => {
    if (isErrorVersions) {
      onError(errorVersions);
    }
  }, [errorVersions, isErrorVersions, onError]);

  useEffect(() => {
    if (isErrorModules) {
      onError(errorModules);
    }
  }, [errorModules, isErrorModules, onError]);

  return {
    softwareNames: {
      options: softwareNameOptions ?? [],
      isLoading: isLoadingSoftwareNames,
      setFilter: setSoftwareFilterValue,
    },
    versions: {
      options: versionOptions ?? [],
      isLoading: isLoadingVersions,
      setFilter: setVersionFilterValue,
    },
    modules: {
      options: moduleOptions ?? [],
      isLoading: isLoadingModules,
      setFilter: setModuleFilterValue,
    },
  };
}
